import { GERMAN_POSTCODE_PATTERN } from '@/helper/validation/proxyInput';
import { type CitiesByPostcodeResult, initGetCitiesQuery } from '@/hooks/usePostcodeService';
import { type ChangeEvent, type Dispatch, type RefObject, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface City {
  postcode: string;
  cityName: string;
}

export enum PostcodeSelectCityState {
  UNSET,
  WARNING,
  SINGLE_CITY,
  MULTIPLE_CITIES,
}

export interface ProductOverviewConfig {
  inputClassName: string;
  inputWidth: number;
  inputRef: RefObject<HTMLInputElement>;
  cityClassName: string;
  onKeyDown: (e: any) => void;
}

export interface PostcodeSelectState {
  inputText: string;
  handleInputChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  error: string | null;
  alertState: undefined | 'warning' | 'error';
  cityState: PostcodeSelectCityState;
  cities: string[];
  setOpen: Dispatch<SetStateAction<boolean>>;
  open: boolean;
  handleCloseDialog: (value: string) => void;
  loading: boolean;
  setInitialValue: (city: City) => void;
  setSelectedValue: (city: City) => void;
  resolveFromPostcode: (postcode: string) => void;
  selectedValue: City;
  validateRequired: () => boolean;
}

export const usePostcodeSelect = (initial?: City): PostcodeSelectState => {
  const initialPostcode = initial?.postcode ?? '';
  const initialCityName = initial?.cityName ?? '';
  const hasInitialCity = Boolean(initialPostcode && initialCityName);

  const [value, setValue] = useState<City>({
    postcode: initialPostcode,
    cityName: initialCityName,
  });

  const [inputText, setInputText] = useState(initialPostcode);
  const [cities, setCities] = useState<string[]>([]);
  const [helperText, setHelperText] = useState<string | null>('');
  const [alertState, setAlertState] = useState<undefined | 'warning' | 'error'>(undefined);
  const [cityState, setCityState] = useState<PostcodeSelectCityState>(
    hasInitialCity ? PostcodeSelectCityState.SINGLE_CITY : PostcodeSelectCityState.UNSET
  );
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [initialValueIsSet, setInitialValueIsSet] = useState(hasInitialCity);

  const { getCities, loading, error, data } = initGetCitiesQuery();

  const applyCitySelection = (
    city: City,
    nextCityState: PostcodeSelectCityState = PostcodeSelectCityState.SINGLE_CITY
  ) => {
    setValue(city);
    setInputText(city.postcode);
    setCityState(city.postcode && city.cityName ? nextCityState : PostcodeSelectCityState.UNSET);
    setAlertState(undefined);
    setHelperText('');
  };

  // set initial postcode + city (once on mount)
  const setInitialValue = (city: City) => {
    if (!initialValueIsSet) {
      applyCitySelection(city);
      setInitialValueIsSet(true);
    }
  };

  // update postcode + city from external sources (e.g. calculator store)
  const setSelectedValue = (city: City) => {
    applyCitySelection(city);
  };

  // resolve city name when only a postcode is known (e.g. after persist rehydrate)
  const resolveFromPostcode = (postcode: string) => {
    setInputText(postcode);
    setCities([]);
    setValue({ postcode: '', cityName: '' });
    setHelperText('');
    setAlertState(undefined);
    setCityState(PostcodeSelectCityState.UNSET);
    setInitialValueIsSet(true);

    if (GERMAN_POSTCODE_PATTERN.test(postcode)) {
      getCities(postcode);
    } else {
      setAlertState('error');
      setHelperText(t('postcodeInput.textInvalidPostcode'));
    }
  };

  // checks if one or multiple cities have been found
  function handleCityResults(result: CitiesByPostcodeResult) {
    const cityList: string[] = [];
    result.postcodes.forEach((item) => {
      cityList.push(item.city);
    });
    setCities((prevCities) => [...prevCities, ...cityList]);

    const firstCity = cityList[0];
    if (cityList.length === 1 && firstCity) {
      applyCitySelection({ postcode: inputText, cityName: firstCity });
    } else if (cityList.length > 1) {
      setAlertState('warning');
      setHelperText(t('postcodeInput.textMultipleCities'));
      setCityState(PostcodeSelectCityState.MULTIPLE_CITIES);
      setOpen(true);
    }
  }

  // watches graphql data
  useEffect(() => {
    if (data) {
      if (data.postcodes?.length > 0) {
        handleCityResults(data);
      } else {
        setAlertState('error');
        setHelperText(t('postcodeInput.textInvalidPostcode'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); // don't add missing dependencies, leads to endless loop

  // watches error state of graphql
  useEffect(() => {
    if (error) {
      setCities([]);
      setValue({ postcode: '', cityName: '' });
      setAlertState('error');
      setHelperText(t('postcodeInput.textServiceDown'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]); // don't add missing dependencies, leads to endless loop

  const validateRequired = (): boolean => {
    if (!value.postcode && !value.cityName) {
      setAlertState('error');
      setHelperText(t('postcodeInput.textPostcodeIsEmpty'));
      return false;
    }
    return true;
  };

  const handleCloseDialog = (value: string) => {
    setOpen(false);
    setAlertState(undefined);
    setHelperText('');
    if (value) {
      applyCitySelection({ postcode: inputText, cityName: value }, PostcodeSelectCityState.MULTIPLE_CITIES);
    } else {
      setValue({ postcode: '', cityName: '' });
      setInputText('');
      setCityState(PostcodeSelectCityState.UNSET);
    }
  };

  function handleInputChange(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    const text = e.target.value.trim();

    if (inputText === text) {
      return;
    }

    setInputText(text);
    setCities([]);
    setValue({ postcode: '', cityName: '' });
    setHelperText('');
    setAlertState(undefined);
    setCityState(PostcodeSelectCityState.UNSET);

    //! The PLZ must contain exactly five real numbers.
    if (GERMAN_POSTCODE_PATTERN.test(text)) {
      getCities(text);
    } else {
      setAlertState('error');
      setHelperText(t('postcodeInput.textInvalidPostcode'));
    }
  }

  return {
    inputText,
    cities,
    loading,
    error: helperText,
    alertState,
    cityState,
    setOpen,
    open,
    handleInputChange,
    handleCloseDialog,
    setInitialValue,
    setSelectedValue,
    resolveFromPostcode,
    selectedValue: value,
    validateRequired,
  };
};
